import * as manualApiClientConfigs from './config/index.js';
import { getClients } from './get-clients.js';

/**
 * Check that the given environment is valid.
 */
const validateEnvironment = environment => {
  const env = environment.toLowerCase();
  const validEnvironments = ['staging', 'preproduction', 'production'];
  if (validEnvironments.indexOf(env) < 0) {
    throw new TypeError(`Unknown environment: must be one of ${validEnvironments.join(', ')}, got "${env}"`);
  }
};

/**
 * Get the base URL for the set environment.
 */
const getBaseUrl = ({
  apiName,
  baseURLs = {},
  env,
  useProxyApi
}) => {
  const apiClients = getClients();
  const configs = Object.entries(apiClients).reduce((acc, [name, {
    config
  }]) => ({
    ...acc,
    [name]: config
  }), {});
  if (!(apiName in manualApiClientConfigs) && !(apiName in configs)) {
    throw new TypeError(`Could not find a config file for API with name "${apiName}"`);
  }
  const {
    environments = {},
    settings: {
      corsEnabled = false
    } = {}
  } = configs[apiName] || manualApiClientConfigs[apiName] || {};
  validateEnvironment(env);

  // Initially set the base URL to the base URLs from site context
  // (these are typically the proxy URLs to service APIs), however if the baseURL for the
  // specific API name is missing then we fallback to the environment base URL (from
  // environment config in this repository).
  let baseURL = baseURLs[apiName] || environments[env];

  // Where CORS is enabled explicitly in the API config, and the override proxy API
  // preference is not set in options, use the environment base URL (from environment config
  // in this repository).
  if (corsEnabled && !useProxyApi) {
    baseURL = environments[env];
  }
  if (!baseURL) {
    throw new Error(`No base URL could be found for the "${apiName}" "${env}" environment`);
  }
  return baseURL;
};

/**
 * Get a map of baseURLs against api clients for the set environment.
 *
 * @param {'staging' | 'preproduction' | 'production'} env The environment to get the base URLs for.
 * @param {Record<string, string>} baseURLs A map of base URLs to use instead of the defaults.
 *
 * @typedef {keyof typeof manualApiClientConfigs | keyof ReturnType<typeof getClients>} ApiName
 * @returns {Record<ApiName, string>}
 */
export const getBaseUrls = (env, baseURLs = {}) => {
  const allClientKeys = [...Object.keys(manualApiClientConfigs), ...Object.keys(getClients())];
  return allClientKeys.reduce((acc, apiName) => {
    const baseUrl = getBaseUrl({
      apiName,
      baseURLs,
      env
    });
    acc[apiName] = baseUrl;
    return acc;
  }, {});
};
export default getBaseUrl;